import IconButton from '@/global/components/Button/IconButton'
import ImprimirButton from '@/global/components/DataGrid/components/CustomGridToolBar/ImprimirButton'
import { optionsColumn } from '@/global/components/DataGrid/hooks/useDataGridPro'
import { GridColDefPro } from '@/global/components/DataGrid/utils/types'
import { useFilterLocaisCentroStore } from '@/global/components/Selects/filter-select'
import { hasPermission } from '@/hooks/useBackendMenu'
import dayjs from 'dayjs'
import SP, { usePeriodo } from 'global/components/Selects/SelectPeriodo'
import ContentGuide from 'global/guides/ContentGuide'
import { useUrlState } from 'hooks'
import { ExternalLinkIcon, PencilIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { TabelaHorarioFuncionario, trocaHorario } from 'service/api'
import { PaginatedResponse } from 'service/api/types'
import { useStore } from 'store'

const columns: GridColDefPro[] = [
    {
        field: 'Funcionario',
        headerName: 'Funcionário',
        flex: 1,
        valueGetter: ({ value }) => value?.nome,
    },
    {
        field: 'TabelaHorario',
        headerName: 'Tabela de horário',
        flex: 1,
        valueGetter: ({ value }) => value?.nome,
    },
    {
        field: 'dtInicial',
        headerName: 'Data inicial',
        width: 140,
        type: 'date',
        valueGetter: ({ value }) => dayjs.utc(value).format('DD/MM/YYYY'),
    },
    {
        field: 'dtFinal',
        headerName: 'Data final',
        width: 140,
        type: 'date',
        valueGetter: ({ value }) =>
            value ? dayjs.utc(value).format('DD/MM/YYYY') : undefined,
        valueFormatter: ({ value }) => value ?? 'Indeterminada',
    },
    {
        ...optionsColumn,
        field: '__editar',
        renderCell: ({ id, row }) =>
            row.isTrocaAutomatica
                ? null
                : hasPermission(['update', 'TrocaDeTabelaDeHorário'], () => (
                      <IconButton title="Editar" href={`${id}/editar`}>
                          <PencilIcon />
                      </IconButton>
                  )),
    },
    {
        ...optionsColumn,
        field: '__excluir',
        renderCell: ({ id, row }) =>
            row.isTrocaAutomatica
                ? hasPermission(['read', 'TrocaAutomáticaDeHorário'], () => (
                      <IconButton
                          title="Ir para trocas automáticas"
                          href="/manutencao-ponto/troca-automatica-horario"
                      >
                          <ExternalLinkIcon />
                      </IconButton>
                  ))
                : hasPermission(['delete', 'TrocaDeTabelaDeHorário'], () => (
                      <IconButton title="Excluir" href={`${id}/excluir`}>
                          <Trash2Icon className="text-error" />
                      </IconButton>
                  )),
    },
]

export default function RelatorioTrocaHorario() {
    const [isImprimindo, setIsImprimindo] = useState(false)
    const empresaId = useStore(state => state.companyId)
    const [[dataInicial, dataFinal], setPeriodo] = usePeriodo()
    const [{ page, perPage }, setUrlPagination] = useUrlState({
        page: 1,
        perPage: 25,
    })
    const { data, isLoading } = trocaHorario.useFetch<
        PaginatedResponse<TabelaHorarioFuncionario>
    >({
        keepPreviousData: true,
        queryParams: {
            dataInicial,
            dataFinal,
            empresaId,
            page,
            perPage,
        },
    })
    const useFilter = useFilterLocaisCentroStore()
    const rows = (data?.data ?? []).filter(useFilter)

    async function handleClick() {
        setIsImprimindo(true)
        return trocaHorario
            .pdf({
                data: rows.map(({ id }) => id),
                queryParam: {
                    dataInicial,
                    dataFinal,
                    empresaId,
                },
            })
            .finally(() => {
                setIsImprimindo(false)
            })
    }

    return (
        <ContentGuide>
            <ContentGuide.Card>
                <ContentGuide.Header
                    label={[
                        'Manutenção do ponto',
                        'Trocas de tabela de horário',
                    ]}
                >
                    <ContentGuide.Filtros
                        SelectEmpresas
                        SelectCentroCusto
                        SelectLocais
                    >
                        <SP.Root
                            onChange={setPeriodo}
                            value={[dataInicial, dataFinal]}
                            allowedTypes={['periodo', 'competencia']}
                            width={320}
                        >
                            <SP.DateSelector />
                            <SP.Buttons />
                        </SP.Root>
                    </ContentGuide.Filtros>
                </ContentGuide.Header>
                <ContentGuide.DataGrid
                    serverSide
                    loading={isLoading}
                    onAdd
                    rows={rows}
                    columns={columns}
                    totalRows={data?.meta?.total}
                    onPaginationChange={({ pageIndex, pageSize }) => {
                        setUrlPagination({
                            page: pageIndex + 1,
                            perPage: pageSize,
                        })
                    }}
                    defaultPageSize={perPage}
                    components={{
                        Toolbar: () => (
                            <>
                                <div style={{ flex: 1 }} />
                                <ImprimirButton
                                    handleClick={handleClick}
                                    isImprimindo={isImprimindo}
                                    sx={{ marginRight: 1, height: '2.5rem' }}
                                />
                            </>
                        ),
                    }}
                    ToolbarProps={{
                        exportButtonProps: {
                            fileName: `exportacao-troca-tabela-horario`,
                        },
                    }}
                />
            </ContentGuide.Card>
            <Outlet context={{ data: data?.data ?? [] }} />
        </ContentGuide>
    )
}

export function useTrocaTabelaHorarioContext() {
    return useOutletContext<{
        data?: TabelaHorarioFuncionario[]
    }>()
}
